import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Search, Filter, Download, ArrowDownRight, CheckCircle, XCircle } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import api from '../../api/axios';
import styles from './Payments.module.css';

const Payments = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/payments/all');
      setTransactions(data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load payment transactions');
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!transactions.length) {
      toast.error('No data to export');
      return;
    }
    
    toast.loading('Generating PDF...', { id: 'pdfExport' });
    const element = reportRef.current;
    const opt = {
      margin:       0.5,
      filename:     `Society_Payments_Report_${new Date().toISOString().split('T')[0]}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, logging: false },
      jsPDF:        { unit: 'in', format: 'a4', orientation: 'landscape' }
    };

    html2pdf().set(opt).from(element).save().then(() => {
        toast.success('Statement exported successfully', { id: 'pdfExport' });
    }).catch(err => {
        toast.error('Failed to generate PDF', { id: 'pdfExport' });
    });
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Payment History" 
        breadcrumb={['Society Management', 'Payments']} 
        actionButton={
          <button className={styles.exportBtn} onClick={handleExport}>
            <Download size={16} /> Export Statement
          </button>
        }
      />

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input type="text" placeholder="Search by Txn ID or desc..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Receipt / Order ID</th>
                <th>Date</th>
                <th>Resident</th>
                <th>Description</th>
                <th>Method</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7">Loading...</td></tr> : transactions.map(txn => (
                <tr key={txn._id}>
                  <td>
                    <strong>{txn.receiptNumber || txn.razorpayOrderId}</strong>
                  </td>
                  <td>{new Date(txn.createdAt).toLocaleDateString('en-GB')}</td>
                  <td>
                    {txn.resident ? `${txn.resident.wing || ''}-${txn.resident.flatNumber || ''} | ${txn.resident.fullName}` : 'Unknown'}
                  </td>
                  <td>Maintenance - {txn.maintenance?.billingMonth}</td>
                  <td><span className={styles.badgeMethod}>{txn.paymentMethod || 'Razorpay'}</span></td>
                  <td>
                    <div className={`${styles.amount} ${styles.credit}`}>
                      <ArrowDownRight size={14} />
                      ₹{txn.amount}
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles[txn.status.toLowerCase()]}`}>
                      {txn.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.mobileList}>
            {loading ? <div style={{padding: '20px', textAlign: 'center'}}>Loading...</div> : transactions.map(txn => (
              <div key={txn._id} className={styles.mobileCard}>
                <div className={styles.cardTop}>
                  <div className={styles.txnMeta}>
                    <strong>Maintenance - {txn.maintenance?.billingMonth}</strong>
                    <span>{txn.receiptNumber || txn.razorpayOrderId} • {new Date(txn.createdAt).toLocaleDateString('en-GB')}</span>
                    <span style={{fontSize: '12px', color: '#64748b'}}>
                      {txn.resident ? `${txn.resident.wing || ''}-${txn.resident.flatNumber || ''} | ${txn.resident.fullName}` : ''}
                    </span>
                  </div>
                  <span className={`${styles.badgeStatus} ${styles[txn.status.toLowerCase()]}`}>
                    {txn.status}
                  </span>
                </div>
                <div className={styles.cardBottom}>
                  <span className={styles.badgeMethod}>{txn.paymentMethod || 'Razorpay'}</span>
                  <div className={`${styles.amount} ${styles.credit}`}>
                    <ArrowDownRight size={14} />
                    ₹{txn.amount}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Hidden PDF Template */}
      <div style={{ display: 'none' }}>
        <div ref={reportRef} className={styles.pdfReport}>
          <div className={styles.pdfHeader}>
            <div className={styles.pdfLogo}>
              <img src="/favicon.jpeg" alt="Logo" />
              <div>
                <h2>SANSKRUTI SHUBHANGAN</h2>
                <p>A COMMUNITY A BETTER TOMORROW</p>
              </div>
            </div>
            <div className={styles.pdfTitle}>
              <h1>Payments Statement</h1>
              <p>Generated on: {new Date().toLocaleDateString('en-GB')}</p>
            </div>
          </div>
          
          <div className={styles.pdfSummary}>
            <div className={styles.summaryBox}>
              <p>Total Transactions</p>
              <h3>{transactions.length}</h3>
            </div>
            <div className={styles.summaryBox}>
              <p>Total Collected Amount</p>
              <h3>₹{transactions.filter(t => t.status === 'Successful').reduce((acc, curr) => acc + curr.amount, 0).toLocaleString('en-IN')}</h3>
            </div>
          </div>

          <table className={styles.pdfTable}>
            <thead>
              <tr>
                <th>Receipt / Order ID</th>
                <th>Date</th>
                <th>Resident</th>
                <th>Description</th>
                <th>Method</th>
                <th>Status</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(txn => (
                <tr key={txn._id}>
                  <td>{txn.receiptNumber || txn.razorpayOrderId}</td>
                  <td>{new Date(txn.createdAt).toLocaleDateString('en-GB')}</td>
                  <td>{txn.resident ? `${txn.resident.wing || ''}-${txn.resident.flatNumber || ''} | ${txn.resident.fullName}` : 'Unknown'}</td>
                  <td>Maint - {txn.maintenance?.billingMonth}</td>
                  <td>{txn.paymentMethod || 'Razorpay'}</td>
                  <td>{txn.status}</td>
                  <td style={{textAlign: 'right'}}>₹{txn.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div className={styles.pdfFooter}>
            <p>This is a system generated statement and does not require a signature.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payments;
